import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from '@/components/ui/form';
import { useAuthorization } from '@/hooks/use-authorization';
import AddressForm from '../addess/address-form';
import { FormSection, FormSectionFullWidth } from '../form-section';
import { RhythmFieldsForm } from '../rhythm-fields';
import { SignatureForm } from '../signature/signature-form';
import {
  ContractProfileFields,
  requiresLicenceFields,
} from './contract-profile-fields';
import { DoctorProfileFormValues } from './schema';
import { BankingToolsSection } from './sections/banking-tools-section';
import {
  ContractDocuments,
  type DoctorProfileFormDocuments,
} from './sections/contract-documents';
import { IdentitySection } from './sections/identity-section';
import { OrderStatusSection } from './sections/order-status-section';
import { DoctorProfileFormMode, isFieldVisible } from './types';
import { DoctorProfileUseForm } from './use-doctor-profile-form';

export interface DoctorProfileFormProps {
  form: DoctorProfileUseForm;
  mode: DoctorProfileFormMode;
  /**
   * Initial data for resetting/initializing form values
   * Used by viewmodels to set initial state
   */
  initialFormData?: Partial<DoctorProfileFormValues>;
  /**
   * Supporting documents of the edited substitute (substitute modes). When
   * given, their rows are shown inside the "Profil contrat" section, gated by
   * the live thesis value; uploads are saved immediately.
   */
  documents?: DoctorProfileFormDocuments;
  /**
   * Callback when avatar is deleted
   */
  onDeleteAvatar?: () => void;
  /**
   * Callback when signature is deleted (`attending-admin` mode only)
   */
  onDeleteSignature?: () => void;
}

/**
 * Unified Doctor Profile Form
 *
 * Consolidates the former EditUserProfileForm, UpdateDoctorSubstituteForm and
 * AddOfficeDoctorAttendingForm into a single component with mode-based
 * field visibility. Fields are grouped into titled sections laid out on two
 * columns from `lg` up.
 */
export const DoctorProfileForm = ({
  form,
  mode,
  initialFormData,
  documents,
  onDeleteAvatar,
  onDeleteSignature,
}: DoctorProfileFormProps) => {
  const { t } = useTranslation();
  const { canEditDoctorFinancials, isAdmin, isSupport, isCsm } =
    useAuthorization();
  // Priority is a staff ranking: only admin/support/csm see and edit it.
  const showPriority =
    isFieldVisible(mode, 'priority') && (isAdmin() || isSupport() || isCsm());

  // Sensitive financial fields (bank details, commission) are
  // read-only for users without can_edit_doctor_financials (e.g. csm) when an
  // admin edits an attending doctor. Self-edit modes are unaffected.
  const financialsDisabled =
    mode === 'attending-admin' && !canEditDoctorFinancials();

  // Reset form when initialFormData changes (for edit modals)
  useEffect(() => {
    if (initialFormData) {
      form.reset(initialFormData);
    }
  }, [initialFormData]);

  const signatureUrl = form.watch('signatureUrl');
  // Only an admin filling an attending's file may import a scan or delete the
  // signature: doctors draw their own and replace it, never delete it.
  const isAdminSignature = mode === 'attending-admin';
  const thesis = form.watch('thesis');
  const showContractProfile = isFieldVisible(mode, 'contractProfile');

  return (
    <Form {...form}>
      <form className="grid gap-6">
        <IdentitySection
          form={form}
          mode={mode}
          onDeleteAvatar={onDeleteAvatar}
        />

        {isFieldVisible(mode, 'address') && (
          <FormSection title={t('edit-user-profile-form.section.address')}>
            <FormSectionFullWidth>
              <AddressForm form={form as any} />
            </FormSectionFullWidth>
          </FormSection>
        )}

        <OrderStatusSection
          form={form}
          mode={mode}
          showPriority={showPriority}
        />

        {showContractProfile && (
          <FormSection
            title={t('edit-user-profile-form.section.contract-profile')}
          >
            <ContractProfileFields form={form} />
            {documents && (
              <ContractDocuments
                documents={documents}
                thesis={!requiresLicenceFields(thesis)}
              />
            )}
          </FormSection>
        )}

        {isFieldVisible(mode, 'rhythm') && (
          <FormSection title={t('edit-user-profile-form.section.rhythm')}>
            <FormSectionFullWidth>
              <RhythmFieldsForm form={form} />
            </FormSectionFullWidth>
          </FormSection>
        )}

        <BankingToolsSection
          form={form}
          mode={mode}
          financialsDisabled={financialsDisabled}
        />

        {isFieldVisible(mode, 'signature') && (
          <FormSection title={t('edit-user-profile-form.section.signature')}>
            <FormSectionFullWidth>
              <SignatureForm
                form={form}
                fieldName="signatureFile"
                label={t('edit-user-profile-form.signature.label')}
                currentSignature={signatureUrl}
                mode={isAdminSignature ? 'admin' : 'own'}
                onDeleteSignature={
                  isAdminSignature
                    ? () => {
                        form.setValue('signatureUrl', null);
                        onDeleteSignature?.();
                      }
                    : undefined
                }
              />
            </FormSectionFullWidth>
          </FormSection>
        )}
      </form>
    </Form>
  );
};

export default DoctorProfileForm;
